// Contexto de Plan — expone plan, canUseModule(), quota y refreshQuota()
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import apiService from '@/services/api';
import { useOrganization } from './OrganizationContext';

// Espejo del backend planLimits.js — fuente de verdad para el frontend
const PLAN_INCLUDED_MODULES: Record<string, string[]> = {
  starter:    ['reports'],
  standard:   ['access_logs', 'bulk_import', 'reports', 'report_denied_access'],
  pro:        ['access_logs', 'audit_logs', 'bulk_import', 'visitors', 'bookings', 'parking', 'reports', 'abac',
               'report_denied_access', 'report_user_trace', 'report_zone_usage'],
  enterprise: ['access_logs', 'audit_logs', 'bulk_import', 'visitors', 'bookings', 'parking', 'noc', 'reports', 'abac',
               'biometrics', 'lpr', 'internal_api', 'compliance',
               'report_denied_access', 'report_user_trace', 'report_zone_usage'],
};

// Tipos de credencial permitidos por plan (mirrors backend PLAN_CREDENTIAL_TYPES)
const PLAN_CREDENTIAL_TYPES: Record<string, string[] | null> = {
  starter:    ['rfid', 'qr', 'cedula_qr', 'national_id', 'pin'],
  standard:   ['rfid', 'qr', 'cedula_qr', 'qr_employee', 'national_id', 'pin', 'rfid_tag', 'tag_vehicular'],
  pro:        ['rfid', 'qr', 'cedula_qr', 'qr_employee', 'national_id', 'pin', 'rfid_tag', 'tag_vehicular'],
  enterprise: null, // todos los tipos base
};

// Tipos que requieren add-on de módulo (mirrors backend ADDON_REQUIRED_TYPES)
const ADDON_REQUIRED_CREDENTIAL_TYPES: Record<string, string> = {
  fingerprint:   'biometrics',
  face:          'biometrics',
  license_plate: 'lpr',
};

export interface QuotaResource {
  limit: number | null;
  current: number;
  pct: number | null;
  warn: boolean;
  exceeded: boolean;
}

export interface HubSetupWindow {
  active: boolean;
  expires_at: string | null;
  permanent: boolean;
}

export interface PlanQuota {
  plan: string;
  resources: Record<string, QuotaResource>;
  temporary_unlocks?: string[];
  hub_setup_window?: HubSetupWindow;
}

interface PlanContextType {
  plan: string;
  canUseModule: (name: string) => boolean;
  canUseCredentialType: (type: string) => boolean;
  quota: PlanQuota | null;
  refreshQuota: () => Promise<void>;
  hubSetupWindowActive: boolean;
  hubSetupWindowExpiresAt: string | null;
  /** Config de organización: ocultar del menú los módulos no incluidos en el plan (en vez de
   *  mostrarlos con candado). Disponible para cualquier plan — ver
   *  organization.config.ui_settings.hide_unavailable_modules. */
  hideUnavailableModules: boolean;
}

const PlanContext = createContext<PlanContextType | undefined>(undefined);

export const PlanProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { organization } = useOrganization();
  const [quota, setQuota] = useState<PlanQuota | null>(null);

  const loadQuota = useCallback(async () => {
    if (!localStorage.getItem('user')) return;
    try {
      const res = await apiService.get<{ success: boolean; data: PlanQuota }>(
        '/api/organizations/quota'
      );
      if (res.success && res.data) setQuota(res.data);
    } catch {
      // fail silently — no bloquear la app por un error de cuota
    }
  }, []);

  useEffect(() => {
    loadQuota();
  }, [loadQuota]);

  useEffect(() => {
    const onLogin = () => loadQuota();
    const onLogout = () => setQuota(null);
    window.addEventListener('auth-login', onLogin);
    window.addEventListener('auth-logout', onLogout);
    return () => {
      window.removeEventListener('auth-login', onLogin);
      window.removeEventListener('auth-logout', onLogout);
    };
  }, [loadQuota]);

  const plan = organization?.plan ?? quota?.plan ?? 'starter';

  const canUseModule = useCallback(
    (name: string): boolean => {
      const included = (PLAN_INCLUDED_MODULES[plan] ?? []).includes(name);
      const addon = organization?.modules?.[name] === true;
      const temporarilyUnlocked = (quota?.temporary_unlocks ?? []).includes(name);
      return included || addon || temporarilyUnlocked;
    },
    [plan, organization?.modules, quota?.temporary_unlocks]
  );

  const canUseCredentialType = useCallback(
    (type: string): boolean => {
      // id_pin es el método de verificación del AP que usa credenciales pin
      const resolvedType = type === 'id_pin' ? 'pin' : type;

      // Tipos que requieren add-on de módulo (biometrics / lpr)
      const requiredAddon = ADDON_REQUIRED_CREDENTIAL_TYPES[resolvedType];
      if (requiredAddon) {
        const included = (PLAN_INCLUDED_MODULES[plan] ?? []).includes(requiredAddon);
        const addon = organization?.modules?.[requiredAddon] === true;
        return included || addon;
      }
      // Resto: verificar según plan
      const allowed = PLAN_CREDENTIAL_TYPES[plan];
      if (allowed === null) return true; // enterprise: todos los tipos
      return Array.isArray(allowed) && allowed.includes(resolvedType);
    },
    [plan, organization?.modules]
  );

  const hubSetupWindowActive = quota?.hub_setup_window?.active ?? (plan === 'pro' || plan === 'enterprise');
  const hubSetupWindowExpiresAt = quota?.hub_setup_window?.expires_at ?? null;
  const hideUnavailableModules = organization?.config?.ui_settings?.hide_unavailable_modules === true;

  return (
    <PlanContext.Provider value={{ plan, canUseModule, canUseCredentialType, quota, refreshQuota: loadQuota, hubSetupWindowActive, hubSetupWindowExpiresAt, hideUnavailableModules }}>
      {children}
    </PlanContext.Provider>
  );
};

export const usePlan = () => {
  const ctx = useContext(PlanContext);
  if (!ctx) throw new Error('usePlan debe ser usado dentro de PlanProvider');
  return ctx;
};

export default PlanContext;
